// FontAwesome: prevent icon FOUC in SSR.
// The react-fontawesome runtime normally injects its critical CSS
// (`.svg-inline--fa { width: 1em; ... }`) on the client side. During SSR
// the browser renders icons before that CSS is applied and paints each
// SVG at its intrinsic viewBox size (huge). Importing the CSS here and
// disabling auto-injection makes the styles part of the SSR/CSS bundle,
// so icons come sized from the very first paint.
// This must run before any component that imports FontAwesomeIcon.
import { config as faConfig } from '@fortawesome/fontawesome-svg-core';
import '@fortawesome/fontawesome-svg-core/styles.css';
import Login from '@plone/volto/components/theme/Login/Login';

// Import existing components
import ComponentsShowcaseView from './components/Routes/ComponentsShowcaseView';
import MultimediaCompShowcaseView from './components/Routes/MultimediaCompShowcaseView';
import PageHeaderCompShowcaseView from './components/Routes/PageHeaderCompShowcaseView';
import CTABannerCompShowcaseView from './components/Routes/CTABannerCompShowcaseView';
import EnhancedRichTextWidget from './components/Widgets/SlateWithPreview';
import DisseminationPage from './components/Routes/DisseminationPage';
import ContactFormPage from './components/Routes/ContactFormPage';
import NewsItemESSView from './components/Views/NewsItemESSView/NewsItemESSView';
import SimpleNewsListing from './components/Views/SimpleNewsListing';
import SearchView from './components/Views/SearchView';
import TeaserGalleryShowcaseView from './components/Routes/TeaserGalleryShowcaseView';
import CookieConsent from './components/Organisms/CookieConsent/CookieConsent';
import ResourceView from './components/Views/ResourceView/ResourceView';
import CompetenceView from './components/Views/CompetenceView/CompetenceView';
import ProjectView from './components/Views/ProjectView/ProjectView';
import PublicationItemView from './components/Views/PublicationItemView/PublicationItemView';
import OurWorkView from './components/Views/OurWorkView';
import WhoWeAreView from './components/Views/WhoWeAreView';
import HomeView from './components/Views/HomeView/HomeView';
import NotFound from './components/Views/NotFound/NotFound';
import PublicationsView from './components/Views/PublicationsView';
import SectorView from './components/Views/SectorView/SectorView';
import ServiceView from './components/Views/ServiceView/ServiceView';
import TeamShowcaseView from './components/Views/TeamShowcaseView/TeamShowcaseView';
import ResourceListView from './components/Views/ResourceListView/ResourceListView';
import SimplePageView from './components/Views/SimplePageView/SimplePageView';
import GridShowcase from './components/Views/GridShowcase';

// Import theme (includes @plone/components/dist/basic.css)
import './theme.js';

// SSR data loading: asyncPropsExtenders for HomeView
import {
  fetchHomeContent,
  fetchTeams,
  fetchNews,
  fetchPartners,
  fetchHighlightedWork,
} from './components/Views/HomeView/homeApi';
import { validateType } from './helpers/contentValidators';

faConfig.autoAddCss = false;

const serverAction = (key, fetcher) => ({
  key,
  promise: ({ store: { dispatch } }) => {
    if (!__SERVER__) return;
    return fetcher(dispatch);
  },
});

const homeAsyncPropsExtender = {
  path: '/',
  exact: true,
  extend: (dispatchActions) => {
    let homePromise;

    dispatchActions.push(serverAction('homeOurTeams', fetchTeams));

    dispatchActions.push(serverAction('homeLatestNews', fetchNews));

    // /home content – single shared promise for homeContent, homePartners and homeHighlightedWork
    dispatchActions.push({
      key: 'homeContent',
      promise: ({ store: { dispatch } }) => {
        if (!__SERVER__) return;
        homePromise ??= fetchHomeContent(dispatch);
        return homePromise;
      },
    });

    dispatchActions.push({
      key: 'homePartners',
      promise: ({ store: { dispatch } }) => {
        if (!__SERVER__) return;
        homePromise ??= fetchHomeContent(dispatch);
        return homePromise.then((homeContent) =>
          fetchPartners(dispatch, homeContent),
        );
      },
    });

    dispatchActions.push({
      key: 'homeHighlightedWork',
      promise: ({ store: { dispatch } }) => {
        if (!__SERVER__) return;
        homePromise ??= fetchHomeContent(dispatch);
        return homePromise.then((homeContent) => {
          const highlightedWork = homeContent?.highlighted_work || [];
          const validHighlightedWork = highlightedWork.filter((item) =>
            validateType(item, ['project', 'sector', 'service', 'competence']),
          );
          return fetchHighlightedWork(dispatch, validHighlightedWork);
        });
      },
    });

    return dispatchActions;
  },
};

const applyConfig = (config) => {
  config.settings.isMultilingual = false;
  config.settings.supportedLanguages = ['es'];
  config.settings.defaultLanguage = 'es';
  config.settings.nonContentRoutes = [
    ...(config.settings.nonContentRoutes || []),
    '/volto-login',
  ];
  config.settings.nonContentRoutesPublic = [
    ...(config.settings.nonContentRoutesPublic || []),
    '/volto-login',
  ];

  // Register SSR async props extender for HomeView
  config.settings.asyncPropsExtenders = [
    ...(config.settings.asyncPropsExtenders || []),
    homeAsyncPropsExtender,
  ];

  // Navigation configuration - Enable dropdown menus for child pages
  config.settings.navDepth = 3; // Fetch 3 levels of navigation hierarchy

  // Footer variant selection
  // Options: 'default', 'compact', 'minimal' (when implemented)
  config.settings.footerVariant = 'default';

  // TEST: Uncomment to test footer customization
  config.settings.footerConfig = {
    secondaryLogo: null,
    contactText: 'For all enquiries, please email us using the form below.',
    contactButtonText: 'contact us',
    contactUrl: '/contact-info',
    privacyUrl: '/privacy-policy',
    legalUrl: 'https://www.bsc.es/legal-notice',
    socialMedia: [
      { name: 'Twitter', url: 'https://x.com/bsc_cns', icon: 'twitter' },
      {
        name: 'Facebook',
        url: 'https://www.facebook.com/BSCCNS',
        icon: 'facebook',
      },
      {
        name: 'Youtube',
        url: 'https://www.youtube.com/user/BSCCNS',
        icon: 'youtube',
      },
      {
        name: 'LinkedIn',
        url: 'https://www.linkedin.com/company/barcelona-supercomputing-center',
        icon: 'linkedin',
      },
      {
        name: 'Instagram',
        url: 'https://www.instagram.com/bsc_cns/',
        icon: 'instagram',
      },
    ],
  };

  // Theme components visibility configuration
  // Control which theme components are displayed globally
  config.settings.themeSettings = {
    showBreadcrumbs: false, // Show/hide breadcrumbs navigation
    showHeader: true, // Show/hide header
    showFooter: true, // Show/hide footer
  };

  // Header configuration (optional - override defaults as needed)
  // See docs/HEADER-CONFIGURATION.md for full documentation

  // TEST: Active configuration to see changes
  config.settings.headerConfig = {
    // Visibility toggles - SET TO FALSE TO SEE THE CHANGE
    showLanguageSelector: false, // ❌ HIDE language selector
    showSearch: true, // ❌ HIDE search widget
    showToolsWrapper: false,
    showAnontools: false,
    showNavigation: true,
    showMobileNavigation: true,
    showLogo: true,
    showSiteActions: true,

    siteLabel: 'ESS website',

    // Layout options
    searchPosition: 'right',
    logoPosition: 'left',

    // Styling options
    //headerBackgroundColor: '#1a1a2e', // ✅ Dark blue background
    //headerTextColor: '#000',
    toolsWrapperBackground: '#16213e',

    // Behavior
    stickyHeader: false, // ✅ Make header sticky
    hideHeaderOnScroll: false,
  };

  // Register content type views
  // IMPORTANT: Use spread operator to preserve existing views
  // home_view registration in layoutViews is required because the component now uses asyncConnect,
  // which alters how it is evaluated during the Server-Side Rendering (SSR) lifecycle.
  // Before adding asyncConnect, Volto fell back to explicit addonRoutes or contentTypesViews to render the UI.
  // However, during the SSR data-fetching phase, Volto's router intercepts the request and looks strictly for
  // the component mapped to the layout property of Plone's JSON response—which, for the root 'Plone Site' object,
  // defaults to home_view.
  config.views = {
    ...config.views,
    contentTypesViews: {
      ...config.views.contentTypesViews,
      news_item_ess: NewsItemESSView,
      resource: ResourceView,
      success_story: ResourceView,
      simple_page: SimplePageView,
      project: ProjectView,
      our_work: OurWorkView,
      who_we_are: WhoWeAreView,
      news: SimpleNewsListing,
      multimedia: DisseminationPage,
      resource_list: ResourceListView,
      written_publication: PublicationItemView,
      oral_publication: PublicationItemView,
      publications: PublicationsView,
      competence: CompetenceView,
      sector: SectorView,
      service: ServiceView,
      service_showcase: TeamShowcaseView,
      home: HomeView,
      Folder: NotFound, 
      'Plone Site': HomeView,
    },
    errorViews: {
      ...config.views.errorViews,
      404: NotFound,
    },
  };

  config.views.itemViewFinder = () => null; // Disable auto child view rendering in listings

  config.addonRoutes = [
    ...(config.addonRoutes || []),
    {
      path: ['/volto-login', '/**/volto-login'],
      component: Login,
      exact: true,
    },
    {
      path: ['/login', '/**/login'],
      component: NotFound,
      exact: true,
    },
    // Custom Search View - Override default Volto search (not editable)
    // FORCE HOOK ASYNC_CONNECT ON THE ROOT PATH DIRECTLY
    {
      path: '/',
      component: HomeView,
      exact: true,
    },
    {
      path: '/home',
      component: HomeView,
      exact: true,
    },
    {
      path: '/search',
      component: SearchView,
      exact: true,
    },
    // Static routes for content pages (moved from SmartFolderView)
    // These routes are not editable from Volto but work with custom content types
    {
      path: '/contact-info',
      component: ContactFormPage,
      exact: true,
    },
    // Development/showcase routes (not editable)
    {
      path: '/components-showcase',
      component: ComponentsShowcaseView,
      exact: true,
    },
    {
      path: '/multimedia-components-showcase',
      component: MultimediaCompShowcaseView,
      exact: true,
    },
    {
      path: '/pageheader-components-showcase',
      component: PageHeaderCompShowcaseView,
      exact: true,
    },
    {
      path: '/ctabanner-components-showcase',
      component: CTABannerCompShowcaseView,
      exact: true,
    },
    {
      path: '/grid-showcase',
      component: GridShowcase,
      exact: true,
    },
    {
      path: '/teaser-gallery-showcase',
      component: TeaserGalleryShowcaseView,
      exact: true,
    },
  ];

  // Register custom widget for richtext fields
  config.widgets.widget = {
    ...config.widgets.widget,
    richtext: EnhancedRichTextWidget,
  };

  // Add Cookie Consent component to appExtras
  // GA4 ID is read from environment variable RAZZLE_GA4_CODE
  // Set it in frontend/.env file: RAZZLE_GA4_CODE=G-XXXXXXXXXX
  config.settings.appExtras = [
    ...(config.settings.appExtras || []),
    {
      match: '',
      component: CookieConsent,
    },
  ];

  // Store GA4 ID in config for access throughout the app
  // Priority: 1) Environment variable, 2) Hardcoded fallback for development
  // For production, set RAZZLE_GA4_CODE environment variable
  const ga4Id = process.env.RAZZLE_GA4_CODE || 'G-M7E7TDPQ9Q';
  config.settings.googleAnalyticsId = ga4Id;

  // Debug log to verify GA4 ID is being set
  return config;
};

// Export components for external use
export {
  NewsItemESSView,
  DisseminationPage,
  ContactFormPage,
  EnhancedRichTextWidget,
  ComponentsShowcaseView,
  NotFound,
};

// Re-export Atoms for external consumption
export * from './components/Atoms';

// Re-export Molecules for external consumption
export * from './components/Molecules';

export default applyConfig;
